import { createSignal, onCleanup } from "solid-js";

/**
 * Reactive `matchMedia`, for the cases where a breakpoint has to drive
 * behaviour rather than styling.
 *
 * Reach for a CSS variant first. This only earns its place when the value has
 * to reach JS — an attribute like `inert`, say, which has no Tailwind variant.
 *
 * Note this is not what `useIsMobile` does: a `createMemo` wrapped around
 * `matchMedia().matches` reads no signal, so it computes once and never runs
 * again. This subscribes to `change` and actually tracks the viewport.
 *
 * Pass the query in `rem` to stay in lockstep with Tailwind, whose breakpoints
 * are `rem`-based — a reader who has bumped their browser font size then moves
 * both together.
 */
export function useMediaQuery(query: string): () => boolean {
  const mql = window.matchMedia(query);
  const [matches, setMatches] = createSignal(mql.matches);
  const handler = (event: MediaQueryListEvent) => setMatches(event.matches);

  mql.addEventListener("change", handler);
  onCleanup(() => mql.removeEventListener("change", handler));

  return matches;
}
